// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Admin panel  (shared modal dialog)
// A small accessible dialog: role="dialog" + aria-modal, focus moves in on open and is kept inside (Tab / Shift+Tab wrap), Escape and the close button
// call onClose, the page behind doesn't scroll, and focus goes back to whatever opened it. Used by the onboarding form and the KYC re-upload / reject prompts.
// Neighbours: pages/admin/AdminDrivers, pages/admin/AdminDriverKyc.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef, type KeyboardEvent, type ReactNode } from "react";
import { X } from "lucide-react";

export default function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    ref.current?.querySelector<HTMLElement>("input:not([disabled]), select, textarea, button:not([disabled])")?.focus();
    return () => {
      document.body.style.overflow = previous;
      opener?.focus?.();
    };
  }, []);
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") { event.stopPropagation(); onClose(); return; }
    if (event.key !== "Tab" || !ref.current) return;
    const items = [...ref.current.querySelectorAll<HTMLElement>("a[href], button:not([disabled]), input:not([disabled]), select, textarea")].filter((el) => el.offsetParent !== null);
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center overflow-y-auto bg-ocean-950/60 p-4" onKeyDown={onKeyDown}>
      <div ref={ref} role="dialog" aria-modal="true" aria-label={title} className={`w-full ${wide ? "max-w-2xl" : "max-w-xl"} rounded-2xl bg-foam-50 p-5 shadow-2xl dark:bg-ocean-900`}>
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-display text-xl font-bold">{title}</h2>
          <button type="button" onClick={onClose} aria-label={`Close: ${title}`} className="inline-flex h-11 w-11 items-center justify-center rounded-xl hover:bg-ocean-700/10"><X className="h-5 w-5" aria-hidden="true" /></button>
        </div>
        <div className="mt-3">{children}</div>
      </div>
    </div>
  );
}
